import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "@/components/ThemeContext";
import ThemeToggle from "@/components/ThemeToggle";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_luxury-ride-avellino/artifacts/v2iwkqbg_N.C.C.svg";

const navLinks = [
  { label: "Home", path: "/" },
  { label: "Chi Siamo", path: "/chi-siamo" },
  { label: "Servizi", path: "/servizi" },
  { label: "Prenota", path: "/prenota" },
  { label: "Contatti", path: "/contatti" },
];

const mobileMenuVariants = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: "auto" },
  exit: { opacity: 0, height: 0 },
};

function navLinkClass(isActive, isDark) {
  if (isActive) return "text-[#C9A646]";
  return isDark ? "text-white/70 hover:text-white" : "text-[#1A1A1A]/70 hover:text-[#1A1A1A]";
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { isDark } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const toggleMobile = useCallback(() => setMobileOpen((prev) => !prev), []);

  const navbarBg = scrolled
    ? isDark
      ? "bg-black/80 backdrop-blur-xl border-b border-white/10"
      : "bg-white/80 backdrop-blur-xl border-b border-black/10 shadow-sm"
    : "bg-transparent";

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navbarBg}`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <Link to="/" data-testid="navbar-logo" className="flex-shrink-0">
          <img
            src={LOGO_URL}
            alt="Transfer Avellino"
            className={`h-16 w-auto transition-all duration-300 ${!isDark && scrolled ? "brightness-0" : ""}`}
          />
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-link-${link.path.replace("/", "") || "home"}`}
              className={`text-sm tracking-[0.08em] uppercase transition-colors duration-300 ${navLinkClass(location.pathname === link.path, isDark)}`}
            >
              {link.label}
            </Link>
          ))}
          <ThemeToggle />
          <a
            href="tel:+393784035054"
            data-testid="navbar-call-btn"
            className="flex items-center gap-2 bg-[#C9A646] text-[#0A0A0A] px-6 py-2.5 text-sm font-medium tracking-wider uppercase hover:bg-[#E1C16E] transition-all duration-300"
          >
            <Phone className="w-4 h-4" strokeWidth={1.5} />
            Chiama
          </a>
        </div>

        <div className="md:hidden flex items-center gap-3">
          <ThemeToggle />
          <button
            data-testid="mobile-menu-toggle"
            className={`p-2 ${isDark ? "text-white" : "text-[#1A1A1A]"}`}
            onClick={toggleMobile}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            variants={mobileMenuVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3 }}
            className={`md:hidden backdrop-blur-xl border-t overflow-hidden ${
              isDark ? "bg-[#0A0A0A]/95 border-white/10" : "bg-white/95 border-black/10"
            }`}
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.path.replace("/", "") || "home"}`}
                  className={`text-lg tracking-wide transition-colors duration-300 ${
                    location.pathname === link.path
                      ? "text-[#C9A646]"
                      : isDark ? "text-white/70" : "text-[#1A1A1A]/70"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
